import React, { useState, useEffect } from 'react';
import { Reminder, ReminderDayOfWeek, CreateSystemReminderData, UpdateReminderData } from '../models/reminder';

interface ReminderModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  reminder?: Reminder | null;
  onSave: (data: CreateSystemReminderData | UpdateReminderData) => Promise<void>;
  onCancel: () => void;
}

const DAY_OPTIONS: { value: ReminderDayOfWeek; label: string }[] = [
  { value: 'Monday', label: 'Lunes' },
  { value: 'Tuesday', label: 'Martes' },
  { value: 'Wednesday', label: 'Miércoles' },
  { value: 'Thursday', label: 'Jueves' },
  { value: 'Friday', label: 'Viernes' },
  { value: 'Saturday', label: 'Sábado' },
  { value: 'Sunday', label: 'Domingo' },
  { value: 'all-week', label: 'Todos los días' },
  { value: 'working-days', label: 'Días laborables' },
  { value: 'weekend', label: 'Fin de semana' },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: 4,
  textTransform: 'uppercase',
};

const ReminderModal: React.FC<ReminderModalProps> = ({ open, mode, reminder, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notificationType, setNotificationType] = useState<'push' | 'email'>('push');
  const [dayOfWeek, setDayOfWeek] = useState<ReminderDayOfWeek>('Monday');
  const [time, setTime] = useState('09:00');
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditing = mode === 'edit';
  // In edit mode, title/description are only editable for system-scoped reminders
  const showTitleDescription = !isEditing || reminder?.scope_type === 'system';

  // Pre-fill form when editing an existing reminder
  useEffect(() => {
    if (isEditing && reminder) {
      setTitle(reminder.title ?? '');
      setDescription(reminder.description ?? '');
      setNotificationType(reminder.notification_type ?? 'push');
      setDayOfWeek((reminder.day_of_week as ReminderDayOfWeek) ?? 'Monday');
      setTime(reminder.time ?? '09:00');
      setEnabled(reminder.enabled ?? true);
      setErrors({});
      setSubmitError(null);
    }
  }, [isEditing, reminder]);

  if (!open) return null;
  if (isEditing && !reminder) return null;

  const reset = () => {
    setTitle('');
    setDescription('');
    setNotificationType('push');
    setDayOfWeek('Monday');
    setTime('09:00');
    setEnabled(true);
    setErrors({});
    setSubmitError(null);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (showTitleDescription && !title.trim()) newErrors.title = 'El título es obligatorio';
    if (!time) newErrors.time = 'La hora es obligatoria';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isEditing) {
        const payload: UpdateReminderData = {
          time,
          day_of_week: dayOfWeek,
          type: notificationType,
          enabled,
        };
        if (showTitleDescription) {
          payload.title = title.trim();
          payload.description = description.trim();
        }
        await onSave(payload);
      } else {
        await onSave({
          scope_type: 'system',
          title: title.trim(),
          description: description.trim(),
          time,
          day_of_week: dayOfWeek,
          type: notificationType,
          enabled,
        } as CreateSystemReminderData);
        reset();
      }
    } catch (err: any) {
      setSubmitError(
        isEditing
          ? 'Error al guardar los cambios. Por favor, inténtalo de nuevo.'
          : 'Error al crear el recordatorio. Por favor, inténtalo de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isEditing) reset();
    onCancel();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '32px 40px',
        maxWidth: 560,
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, fontSize: 20, color: '#1f2937', marginBottom: isEditing && !showTitleDescription ? 8 : 24 }}>
          {isEditing ? 'Editar Recordatorio' : 'Nuevo Recordatorio'}
        </div>

        {isEditing && !showTitleDescription && (
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
            El título y descripción de los recordatorios de usuario están vinculados a la fuente y no son editables.
          </div>
        )}

        {submitError && (
          <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', color: '#c53030', padding: '10px 14px', borderRadius: 6, marginBottom: 20, fontSize: 14 }}>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {showTitleDescription && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Título *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: '' })); }}
                  placeholder="Ej: Tomar suplemento"
                  style={{ ...inputStyle, borderColor: errors.title ? '#e53e3e' : '#d1d5db' }}
                />
                {errors.title && <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{errors.title}</div>}
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Descripción (opcional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Mensaje adicional para el paciente"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Tipo de notificación</label>
            <div style={{ display: 'flex', gap: 12 }}>
              {(['push', 'email'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setNotificationType(type)}
                  style={{
                    flex: 1,
                    padding: '8px 0',
                    borderRadius: 6,
                    border: `2px solid ${notificationType === type ? '#38b2ac' : '#d1d5db'}`,
                    background: notificationType === type ? '#e6fffa' : '#fff',
                    color: notificationType === type ? '#38b2ac' : '#6b7280',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {type === 'push' ? '📲 Push' : '📧 Email'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Día de la semana</label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(e.target.value as ReminderDayOfWeek)}
                style={{ ...inputStyle }}
              >
                {DAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Hora *</label>
              <input
                type="time"
                value={time}
                onChange={(e) => { setTime(e.target.value); setErrors(prev => ({ ...prev, time: '' })); }}
                style={{ ...inputStyle, borderColor: errors.time ? '#e53e3e' : '#d1d5db' }}
              />
              {errors.time && <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{errors.time}</div>}
            </div>
          </div>

          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
              Activado
            </label>
            <button
              type="button"
              onClick={() => setEnabled(prev => !prev)}
              style={{
                width: 40, height: 22, borderRadius: 11,
                border: 'none',
                background: enabled ? '#38b2ac' : '#d1d5db',
                cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
              }}
            >
              <span style={{
                position: 'absolute', top: 3,
                left: enabled ? 21 : 3,
                width: 16, height: 16, borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{
                background: '#fff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                padding: '10px 24px',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: isSubmitting ? '#9ca3af' : '#38b2ac',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 24px',
                fontWeight: 600,
                fontSize: 14,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#319795'; }}
              onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#38b2ac'; }}
            >
              {isSubmitting
                ? (isEditing ? 'Guardando...' : 'Creando...')
                : (isEditing ? 'Guardar Cambios' : 'Crear Recordatorio')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderModal;
